import React, { useState, useEffect } from "react";
import { FaShoppingCart, FaHeart, FaStar, FaBoxOpen, FaFish, FaExchangeAlt } from "react-icons/fa";

const FoodCard = ({ food = {}, onOrderClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [imageLoaded, setImageLoaded] = useState(false);
  const [selectedType, setSelectedType] = useState('flakes'); // 'flakes' or 'powder'


  const {
    name = "طعام غير مسمى",
    flakesPrice = 0,
    powderPrice = 0,
    flakesQuantity = 0,
    powderQuantity = 0,
    image = "",
    sold_status = false,
    basePricePer50g = 0,
    description = "",
    benefits = "",
  } = food;

  // Check if this is fish food (has basePricePer50g)
  const isFishFood = basePricePer50g > 0 ||
    description?.includes('مسحوق سمك') ||
    name?.toLowerCase().includes('سمك') ||
    name?.toLowerCase().includes('fish food');

  const hasFlakes = flakesQuantity > 0 && flakesPrice > 0;
  const hasPowder = powderQuantity > 0 && powderPrice > 0;
  const bothTypesAvailable = hasFlakes && hasPowder;

  // For fish food, use powder/grains instead of flakes/powder
  useEffect(() => {
    if (isFishFood) {
      setSelectedType('powder'); // Default to powder for fish food
    } else {
      if (hasFlakes && !hasPowder) setSelectedType('flakes');
      else if (hasPowder && !hasFlakes) setSelectedType('powder');
      else if (hasFlakes) setSelectedType('flakes');
    }
  }, [hasFlakes, hasPowder, isFishFood]);

  // Calculate price for fish food
  const currentPrice = isFishFood
    ? basePricePer50g
    : (selectedType === 'flakes' ? flakesPrice : powderPrice);
  const currentQuantity = isFishFood
    ? 999 // Fish food is always available
    : (selectedType === 'flakes' ? flakesQuantity : powderQuantity);
  const formattedPrice = typeof currentPrice === "number" ? currentPrice.toFixed(0) : "0";

  const getQuantityColor = (qty) => {
    if (qty === 0) return '#ef4444';
    if (qty <= 5) return '#f59e0b';
    return '#f97316';
  };

  const getQuantityStatus = (qty) => {
    if (qty === 0) return 'نفذت الكمية';
    if (qty <= 5) return 'كمية محدودة';
    return 'متوفر';
  };

  const handleOrderClick = () => {
    if (onOrderClick) {
      const orderItem = {
        ...food,
        id: food.id || `food_${Date.now()}`,
        type: 'food',
        selectedFoodType: isFishFood ? selectedType : (selectedType === 'flakes' ? 'flakes' : 'powder'),
        selectedForm: isFishFood ? selectedType : undefined, // 'powder' or 'grains' for fish food
        price: currentPrice,
        quantity: currentQuantity,
        basePricePer50g: isFishFood ? basePricePer50g : undefined
      };
      onOrderClick(orderItem);
    }
  };

  useEffect(() => {
    const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    setIsLiked(favorites.includes(food.id));
  }, [food.id]);

  const toggleFavorite = () => {
    const newLikedState = !isLiked;
    setIsLiked(newLikedState);

    const favorites = JSON.parse(localStorage.getItem('favorites') || '[]');
    if (newLikedState) {
      if (!favorites.includes(food.id)) {
        localStorage.setItem('favorites', JSON.stringify([...favorites, food.id]));
      }
    } else {
      localStorage.setItem('favorites', JSON.stringify(favorites.filter(id => id !== food.id)));
    }
  };

  const canOrder = !sold_status && currentQuantity > 0;

  return (
    <>
      <style>{`
        .food-card {
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .food-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 20px 40px -5px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(249, 115, 22, 0.3);
        }
        
        .food-image {
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        }
        
        .food-card:hover .food-image {
          transform: scale(1.05);
        }
        
        .heart-button {
          transition: all 0.3s ease;
          backdrop-filter: blur(8px);
        }
        
        .heart-button:hover {
          transform: scale(1.1);
          background: rgba(0, 0, 0, 0.8) !important;
        }
        
        .heart-button.liked {
          animation: heartBeat 0.6s ease;
        }
        
        @keyframes heartBeat {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.2); }
        }
        
        .primary-button {
          background: linear-gradient(135deg, #f97316, #ea580c) !important;
          border: none;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          position: relative;
          overflow: hidden;
        }
        
        .primary-button::before {
          content: '';
          position: absolute;
          top: 0;
          left: -100%;
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent);
          transition: left 0.6s;
        }
        
        .primary-button:hover::before {
          left: 100%;
        }
        
        .primary-button:hover {
          background: linear-gradient(135deg, #ea580c, #c2410c) !important;
          transform: translateY(-2px);
          box-shadow: 0 10px 30px rgba(249, 115, 22, 0.4);
        }
        
        .sold-out-button {
          background: linear-gradient(135deg, rgba(107, 114, 128, 0.3), rgba(75, 85, 99, 0.3));
          color: #9ca3af;
          border: 1px solid rgba(107, 114, 128, 0.4);
        }
        
        .type-switcher {
          backdrop-filter: blur(10px);
          transition: all 0.3s ease;
        }
        
        .type-switcher:hover {
          transform: scale(1.05);
          background: rgba(249, 115, 22, 0.9) !important;
          box-shadow: 0 4px 12px rgba(249, 115, 22, 0.3);
        }
        
        .loading-skeleton {
          background: linear-gradient(90deg, rgba(51, 65, 85, 0.3) 25%, rgba(71, 85, 105, 0.5) 50%, rgba(51, 65, 85, 0.3) 75%);
          background-size: 200% 100%;
          animation: shimmer 1.5s infinite;
        }
        
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>

      <div
        className="food-card"
        style={{
          background: "linear-gradient(135deg, rgba(15, 23, 42, 0.80), rgba(30, 41, 59, 0.68))",
          backdropFilter: "blur(2px)",
          borderRadius: "24px",
          overflow: "hidden",
          boxShadow: "0 10px 25px -5px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(71, 85, 105, 0.2)",
          width: "100%",
          height: "100%",
          minHeight: "580px",
          border: "1px solid rgba(249, 115, 22, 0.2)",
          position: "relative",
          display: "flex",
          flexDirection: "column",
        }}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Status Badge */}
        {(sold_status || (currentQuantity === 0 && !hasFlakes && !hasPowder)) && (
          <div className="floating-badge" style={{
            position: "absolute",
            top: "16px",
            left: "16px",
            background: "linear-gradient(135deg, #ef4444, #dc2626)",
            color: "white",
            padding: "8px 14px",
            borderRadius: "25px",
            fontSize: "12px",
            fontWeight: "bold",
            zIndex: 10,
            boxShadow: "0 6px 20px rgba(239, 68, 68, 0.4)",
            display: "flex",
            alignItems: "center",
            gap: "6px",
          }}>
            <FaStar size={12} />
            نفذت الكمية
          </div>
        )}

        {/* Like Button */}
        <button
          className={`heart-button ${isLiked ? 'liked' : ''}`}
          onClick={toggleFavorite}
          style={{
            position: "absolute",
            top: "16px",
            right: "16px",
            background: "rgba(0, 0, 0, 0.6)",
            border: "none",
            borderRadius: "50%",
            width: "44px",
            height: "44px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: isLiked ? "#ef4444" : "white",
            cursor: "pointer",
            zIndex: 10,
          }}
        >
          <FaHeart size={18} />
        </button>

        {/* Image Container */}
        <div
          className="food-image-container"
          style={{
            width: "100%",
            height: "260px",
            background: "linear-gradient(135deg, rgba(15, 23, 42, 0.25), rgba(30, 41, 59, 0.15))",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            flexShrink: 0,
          }}
        >
          {image ? (
            !imageLoaded ? (
              <div className="loading-skeleton" style={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#64748b",
                fontSize: "48px",
              }}>
                🍽️
              </div>
            ) : null
          ) : (
            <div style={{
              color: "#64748b",
              fontSize: "64px",
              opacity: 0.5,
            }}>
              🍽️
            </div>
          )}

          {image && (
            <img
              className="food-image"
              src={image}
              alt={name}
              loading="lazy"
              style={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: imageLoaded ? 1 : 0,
                position: imageLoaded ? "static" : "absolute",
                transition: "opacity 0.3s ease-in-out",
              }}
              onLoad={() => setImageLoaded(true)}
            />
          )}

          {/* Type Switcher (if both types available - for regular food) */}
          {!isFishFood && bothTypesAvailable && (
            <button
              className="type-switcher"
              onClick={() => setSelectedType(selectedType === 'flakes' ? 'powder' : 'flakes')}
              style={{
                position: "absolute",
                bottom: "12px",
                right: "12px",
                background: "rgba(249, 115, 22, 0.8)",
                color: "white",
                border: "none",
                padding: "10px 14px",
                borderRadius: "25px",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {selectedType === 'flakes' ? 'رقائق' : 'مسحوق'}
              <FaExchangeAlt size={10} />
            </button>
          )}

          {/* Single Type Label */}
          {!isFishFood && !bothTypesAvailable && (hasFlakes || hasPowder) && (
            <div style={{
              position: "absolute",
              bottom: "12px",
              right: "12px",
              background: "rgba(0, 0, 0, 0.8)",
              color: "white",
              padding: "8px 12px",
              borderRadius: "20px",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              backdropFilter: "blur(8px)",
            }}>
              {hasFlakes ? 'رقائق' : 'مسحوق'}
            </div>
          )}

          {/* Fish Food Type Switcher */}
          {isFishFood && (
            <button
              className="type-switcher"
              onClick={() => setSelectedType(selectedType === 'powder' ? 'grains' : 'powder')}
              style={{
                position: "absolute",
                bottom: "12px",
                right: "12px",
                background: "rgba(249, 115, 22, 0.8)",
                color: "white",
                border: "none",
                padding: "10px 14px",
                borderRadius: "25px",
                cursor: "pointer",
                fontSize: "12px",
                fontWeight: "bold",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}
            >
              {selectedType === 'powder' ? 'مسحوق' : 'حبات 1ملم'}
              <FaExchangeAlt size={10} />
            </button>
          )}
        </div>

        {/* Content */}
        <div className="info-container" style={{
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          gap: "16px"
        }}>
          <div style={{
            background: "linear-gradient(135deg, rgba(249, 115, 22, 0.08), rgba(234, 88, 12, 0.08))",
            border: "1px solid rgba(249, 115, 22, 0.2)",
            borderRadius: "16px",
            padding: "16px",
            flex: 1,
          }}>
            {/* Name */}
            <div className="info-row" style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px",
              borderBottom: "1px solid rgba(249, 115, 22, 0.15)",
              marginBottom: "8px",
            }}>
              <span style={{ color: "#f97316", fontWeight: "bold", fontSize: "14px" }}>
                الاسم
              </span>
              <span style={{
                color: "white",
                fontSize: "15px",
                fontWeight: "600",
                textAlign: "right",
                wordBreak: "break-word",
                maxWidth: "60%"
              }}>
                {name}
              </span>
            </div>

            {/* Type */}
            <div className="info-row" style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px",
              borderBottom: "1px solid rgba(249, 115, 22, 0.15)",
              marginBottom: "8px",
            }}>
              <span style={{ color: "#f97316", fontWeight: "bold", fontSize: "14px" }}>
                {isFishFood ? "الشكل" : "النوع"}
              </span>
              <span style={{
                color: "white",
                fontSize: "14px",
                fontWeight: "600",
                padding: "4px 8px",
                borderRadius: "8px",
                background: "rgba(249, 115, 22, 0.15)",
              }}>
                {isFishFood
                  ? (selectedType === 'powder' ? 'مسحوق (غبرة)' : 'حبات صغار (1ملم)')
                  : (selectedType === 'flakes' ? 'رقائق' : 'مسحوق')}
              </span>
            </div>

            {/* Description for fish food */}
            {isFishFood && description && (
              <div className="info-row" style={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                padding: "8px",
                borderBottom: "1px solid rgba(249, 115, 22, 0.15)",
                marginBottom: "8px",
              }}>
                <span style={{ color: "#f97316", fontWeight: "bold", fontSize: "13px" }}>
                  المكونات:
                </span>
                <span style={{
                  color: "#94a3b8",
                  fontSize: "12px",
                  lineHeight: "1.5",
                }}>
                  {description}
                </span>
              </div>
            )}

            {/* Benefits for fish food */}
            {isFishFood && benefits && (
              <div className="info-row" style={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                padding: "8px",
                borderBottom: "1px solid rgba(249, 115, 22, 0.15)",
                marginBottom: "8px",
              }}>
                <span style={{ color: "#f97316", fontWeight: "bold", fontSize: "13px" }}>
                  الفوائد:
                </span>
                <span style={{
                  color: "#10b981",
                  fontSize: "12px",
                  lineHeight: "1.5",
                }}>
                  {benefits}
                </span>
              </div>
            )}

            {/* Price */}
            <div className="info-row" style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px",
              borderBottom: "1px solid rgba(249, 115, 22, 0.15)",
              marginBottom: "8px",
            }}>
              <span style={{ color: "#f97316", fontWeight: "bold", fontSize: "14px" }}>
                السعر
              </span>
              <span className="price-tag" style={{
                color: "#fbbf24",
                fontSize: isFishFood ? "16px" : "18px",
                fontWeight: "bold",
              }}>
                {formattedPrice} {isFishFood ? "دج لكل 50غ" : "DA"}
              </span>
            </div>

            {/* Quantity - only show for non-fish food */}
            {!isFishFood && (
              <div className="info-row" style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px",
                borderBottom: "1px solid rgba(249, 115, 22, 0.15)",
                marginBottom: "8px",
              }}>
                <span style={{ color: "#f97316", fontWeight: "bold", fontSize: "14px" }}>
                  الكمية المتاحة
                </span>
                <span style={{
                  color: getQuantityColor(currentQuantity),
                  fontSize: "14px",
                  fontWeight: "600",
                  padding: "4px 8px",
                  borderRadius: "8px",
                  background: `${getQuantityColor(currentQuantity)}15`,
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                }}>
                  <FaBoxOpen size={12} />
                  {currentQuantity} قطعة
                </span>
              </div>
            )}

            {/* Info for fish food */}
            {isFishFood && (
              <div className="info-row" style={{
                display: "flex",
                flexDirection: "column",
                gap: "4px",
                padding: "8px",
                borderBottom: "1px solid rgba(249, 115, 22, 0.15)",
                marginBottom: "8px",
              }}>
                <span style={{ color: "#10b981", fontSize: "12px", fontWeight: "600" }}>
                  💡 الكميات المتاحة: 50غ، 100غ، 150غ... (مضاعفات 50غ)
                </span>
              </div>
            )}

            {/* Status */}
            <div className="info-row" style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px",
            }}>
              <span style={{ color: "#f97316", fontWeight: "bold", fontSize: "14px" }}>
                الحالة
              </span>
              <span className={canOrder ? "status-available" : ""} style={{
                color: getQuantityColor(currentQuantity),
                fontSize: "14px",
                fontWeight: "600",
                padding: "6px 12px",
                borderRadius: "12px",
                background: `${getQuantityColor(currentQuantity)}15`,
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}>
                <FaFish size={12} />
                {getQuantityStatus(currentQuantity)}
              </span>
            </div>
          </div>

          {/* Action Button */}
          <div style={{ marginTop: "auto" }}>
            {canOrder ? (
              <button
                className="primary-button card-button"
                onClick={handleOrderClick}
                style={{
                  borderRadius: "16px",
                  padding: "14px 20px",
                  color: "white",
                  fontSize: "15px",
                  fontWeight: "bold",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "10px",
                  boxShadow: "0 6px 20px rgba(249, 115, 22, 0.3)",
                  minHeight: "52px",
                  width: "100%",
                }}
              >
                <FaShoppingCart size={16} />
                اطلب الآن
              </button>
            ) : (
              <div className="sold-out-button card-button" style={{
                borderRadius: "16px",
                padding: "14px 20px",
                fontSize: "15px",
                fontWeight: "bold",
                textAlign: "center",
                minHeight: "52px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "10px",
                width: "100%",
              }}>
                <FaStar size={14} />
                تم البيع
              </div>
            )}
          </div>
        </div>

        {isHovered && (
          <div style={{
            position: "absolute",
            inset: 0,
            background: "linear-gradient(135deg, rgba(249, 115, 22, 0.05), rgba(234, 88, 12, 0.05))",
            borderRadius: "24px",
            pointerEvents: "none",
          }} />
        )}
      </div>
    </>
  );
};

export default React.memo(FoodCard);

